/** ISO/IEC 7810 ID-1 card width (credit/ID card) — identical worldwide. */
export const CARD_WIDTH_MM = 85.6;
export const MM_PER_INCH = 25.4;
/** CSS reference: 96px per inch → px per mm at devicePixelRatio 1. */
export const DEFAULT_PX_PER_MM = 96 / MM_PER_INCH;

export type RulerUnit = "cm" | "mm" | "inch";

export interface Tick {
  /** pixels from the ruler origin */
  pos: number;
  size: "major" | "medium" | "minor";
  /** label shown at major ticks */
  label?: string;
}

/** px-per-mm implied when an on-screen card of `cardWidthPx` matches a real card. */
export function pxPerMmFromCard(cardWidthPx: number): number {
  if (!(cardWidthPx > 0)) return DEFAULT_PX_PER_MM;
  return cardWidthPx / CARD_WIDTH_MM;
}

/** Total measured length of `lengthPx` at the given calibration, in the unit. */
export function lengthInUnit(
  lengthPx: number,
  pxPerMm: number,
  unit: RulerUnit,
): number {
  const mm = lengthPx / pxPerMm;
  if (unit === "inch") return mm / MM_PER_INCH;
  if (unit === "cm") return mm / 10;
  return mm;
}

/**
 * Tick marks for a ruler `lengthPx` long at `pxPerMm` calibration.
 * cm/mm use 1 mm steps (major every 10, medium every 5); inch uses 1/16" steps
 * (major every inch, medium at halves/quarters).
 */
export function generateTicks(
  lengthPx: number,
  pxPerMm: number,
  unit: RulerUnit,
): Tick[] {
  const ticks: Tick[] = [];
  if (!(lengthPx > 0) || !(pxPerMm > 0)) return ticks;

  if (unit === "inch") {
    const stepPx = (pxPerMm * MM_PER_INCH) / 16;
    const count = Math.floor(lengthPx / stepPx);
    for (let i = 0; i <= count; i++) {
      let size: Tick["size"] = "minor";
      let label: string | undefined;
      if (i % 16 === 0) {
        size = "major";
        label = String(i / 16);
      } else if (i % 8 === 0 || i % 4 === 0) {
        size = "medium";
      }
      ticks.push({ pos: i * stepPx, size, label });
    }
    return ticks;
  }

  const stepPx = pxPerMm; // 1 mm
  const count = Math.floor(lengthPx / stepPx);
  for (let i = 0; i <= count; i++) {
    let size: Tick["size"] = "minor";
    let label: string | undefined;
    if (i % 10 === 0) {
      size = "major";
      label = unit === "cm" ? String(i / 10) : String(i);
    } else if (i % 5 === 0) {
      size = "medium";
    }
    ticks.push({ pos: i * stepPx, size, label });
  }
  return ticks;
}
